'use client';

import {
  FC,
  PropsWithChildren,
  ReactNode,
  createContext,
  useContext,
  useState,
} from 'react';
import { css } from '@bts-web/utils-style-engine';
import { AnimatePresence } from 'framer-motion';
import { NotificationItem } from './NotificationItem/NotificationItem';

export type NotificationPosition = 'topRight' | 'bottomRight';

export type TNotificationEntry = {
  title?: string;
  subtitle?: string | React.ReactElement;
  action?: { component: ReactNode; altText: string };
  withClose?: boolean;
  visual?: 'info' | 'error' | 'warning' | 'success' | 'neutral';
  type?: 'toast' | 'global';
  id: string;
  customDuration?: number | 'infinite';
  position?: NotificationPosition;
};

export type TNotificationTriggerType = Omit<TNotificationEntry, 'id'> & {
  id?: string;
};

export type INotificationContext = {
  setAppNotification: (notification: TNotificationTriggerType) => void;
  closeNotificationById: (id: string) => void;
  closeAllNotifications: () => void;
};

const NotificationContext = createContext<INotificationContext>({
  setAppNotification: () => {
    return;
  },
  closeNotificationById: () => {
    return;
  },
  closeAllNotifications: () => {
    return;
  },
});

// Container styles for the overlay
const overlayStyles = css({
  position: 'fixed',
  top: 0,
  insetInline: 0,
  bottom: 0,
  pointerEvents: 'none',
  zIndex: 9999,
  isolation: 'isolate',
});

// Group styles for positioning
const getGroupStyles = (position: NotificationPosition, type: string) =>
  css({
    position: 'absolute',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    pointerEvents: 'none',

    ...(position === 'topRight'
      ? {
          top: { base: type === 'global' ? 0 : 16, lg: 72 },
          right: { base: type === 'global' ? 0 : 16, lg: 24 },
        }
      : {
          bottom: { base: type === 'global' ? 0 : 16, lg: 72 },
          right: { base: type === 'global' ? 0 : 16, lg: 24 },
        }),

    width: { base: type === 'global' ? '100%' : '342px', lg: '342px' },
    maxWidth: {
      base: type === 'global' ? '100vw' : 'calc(100vw - 32px)',
      lg: '342px',
    },
  });

// Container component that groups notifications
const NotificationContainer: FC<{
  notifications: TNotificationEntry[];
  onRemove: (id: string) => void;
}> = ({ notifications, onRemove }) => {
  // Group notifications by position and type
  const groupedNotifications = notifications.reduce(
    (acc, notif) => {
      const key = `${notif.position || 'topRight'}-${notif.type || 'toast'}`;

      if (!acc[key]) {
        acc[key] = {
          position: notif.position || 'topRight',
          type: notif.type || 'toast',
          notifications: [],
        };
      }

      acc[key].notifications.push(notif);

      return acc;
    },
    {} as Record<
      string,
      {
        position: NotificationPosition;
        type: string;
        notifications: TNotificationEntry[];
      }
    >,
  );

  return (
    <div data-testid="notification-overlay" className={overlayStyles}>
      {Object.entries(groupedNotifications).map(([key, group]) => (
        <div key={key} className={getGroupStyles(group.position, group.type)}>
          <AnimatePresence mode="sync">
            {group.notifications.map((notification) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onRemove={onRemove}
              />
            ))}
          </AnimatePresence>
        </div>
      ))}
    </div>
  );
};

export const NotificationContextProvider: FC<PropsWithChildren> = ({
  children,
}) => {
  const [notifications, setNotifications] = useState<TNotificationEntry[]>([]);

  const setAppNotification = (notification: TNotificationTriggerType) => {
    // For infinite notifications with a specific ID, check for duplicates
    if (notification.id && notification.customDuration === 'infinite') {
      setNotifications((current) => {
        const exists = current.some(
          (item) =>
            item.id.startsWith(notification.id as string) &&
            item.customDuration === 'infinite',
        );

        if (exists) return current;

        const id = `${notification.id}`;

        const newNotification: TNotificationEntry = {
          ...notification,
          id,
        };

        return [...current, newNotification];
      });
    } else {
      const id = notification.id
        ? `${notification.id}`
        : `notif-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

      const newNotification: TNotificationEntry = {
        ...notification,
        id,
      };

      setNotifications((current) => [...current, newNotification]);
    }
  };

  const closeNotificationById = (id: string) => {
    setNotifications((current) => current.filter((n) => n.id !== id));
  };

  const closeAllNotifications = () => {
    setNotifications([]);
  };

  return (
    <NotificationContext.Provider
      value={{
        setAppNotification,
        closeNotificationById,
        closeAllNotifications,
      }}
    >
      {children}
      <NotificationContainer
        notifications={notifications}
        onRemove={closeNotificationById}
      />
    </NotificationContext.Provider>
  );
};

export const useAppNotification = () => {
  const context = useContext(NotificationContext);

  if (!context) {
    throw new Error(
      'useAppNotification must be used within NotificationContextProvider',
    );
  }

  return context;
};
